import { forwardRef } from 'react';
import { MdAdd } from 'react-icons/md';
import ModeItem from './ModeItem';
import { getModeIcon, getModeText, getTooltipContent, getAvailableModes } from './modeConfig';

interface ModeDropdownProps {
    position: { left: number; top: number };
    isOpen: boolean;
    theme: string;
    currentMode: string;
    environment: string;
    customModes: Record<string, any>;
    onModeChange: (mode: string) => void;
    onMouseEnter: (mode: string) => void;
    onMouseLeave: () => void;
    getItemBackground: (mode: string) => string;
    onAddModeClick: () => void;
}

const ModeDropdown = forwardRef<HTMLDivElement, ModeDropdownProps>(({
    position,
    isOpen,
    theme,
    currentMode,
    environment,
    customModes,
    onModeChange,
    onMouseEnter,
    onMouseLeave,
    getItemBackground,
    onAddModeClick
}, ref) => {
    const availableModes = getAvailableModes(environment);

    return (
        <div className={`${theme} ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-200`}>
            <div
                ref={ref}
                className="fixed z-50"
                style={{
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    transform: 'translateY(-100%)',
                    marginTop: '-5px'
                }}
            >
                <div className="max-h-[40vh] rounded-md mb-1 overflow-auto shadow-[0_2px_3px_1px_rgba(0,0,0,0.15)] min-w-36 max-w-48 border border-neutral-800">
                    <ul>
                        {/* Built-in modes */}
                        {availableModes.map((modeKey) => (
                            <ModeItem
                                key={modeKey}
                                modeKey={modeKey}
                                modeName={getModeText(modeKey, environment, customModes)}
                                icon={getModeIcon(modeKey, customModes)}
                                isSelected={currentMode === modeKey}
                                tooltipContent={getTooltipContent(modeKey, environment, customModes)}
                                onModeChange={onModeChange}
                                onMouseEnter={() => onMouseEnter(modeKey)}
                                onMouseLeave={onMouseLeave}
                                getItemBackground={getItemBackground}
                            />
                        ))}

                        {/* Custom modes */}
                        {Object.entries(customModes).map(([modeKey]: [string, any], index) => (
                            <ModeItem
                                key={modeKey}
                                modeKey={modeKey}
                                modeName={getModeText(modeKey, environment, customModes)}
                                icon={getModeIcon(modeKey, customModes)}
                                isSelected={currentMode === modeKey}
                                tooltipContent={getTooltipContent(modeKey, environment, customModes)}
                                onModeChange={onModeChange}
                                onMouseEnter={() => onMouseEnter(modeKey)}
                                onMouseLeave={onMouseLeave}
                                getItemBackground={getItemBackground}
                                showBorder={index === 0}
                            />
                        ))}
                        <li
                            onClick={onAddModeClick}
                            className="p-2 text-sm cursor-pointer text-black dark:text-white flex items-center bg-neutral-100 dark:bg-neutral-900 hover:bg-neutral-300 dark:hover:bg-neutral-800 border-t border-neutral-600"
                            role="button"
                            aria-label="Add new custom mode"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onAddModeClick();
                                }
                            }}
                        >
                            <MdAdd size={16} className="mr-2" />
                            <span>Add Mode</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
});

export default ModeDropdown;